import { useState } from "react";
import { useNavigate, useLocation } from "@entry/navigation";

import { CodeInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Routes, StorageKey } from "@ctypes";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useCreateTermsConsent,
  useErrorBanner,
  useFetchUser,
  useFetchVerifiedPrefill,
  useLoginWithPhone,
  useOnboardingSavePoint,
  usePrefill,
  useSetUserIdentity,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { formatMaskedSsnNumber, formatPhoneNumber } from "@utils";
import { PhoneLoginRouteProps } from "./types";

const CODE_LENGTH = 6;

export const PhoneLoginRoute = () => {
  const { showErrorBanner } = useErrorBanner();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");

  const [loginWithPhone] = useLoginWithPhone();

  const [navigateToNextRoute] = useOnboardingSavePoint();
  const [fetchVerifiedPrefill] = useFetchVerifiedPrefill();
  const [fetchUser] = useFetchUser();
  const [createTermsConsent] = useCreateTermsConsent();
  const [setUserIdentity] = useSetUserIdentity();
  const { setPrefillProps } = usePrefill();
  const navigate = useNavigate();

  const phoneNumber = (location.state as PhoneLoginRouteProps)?.phoneNumber ?? "";
  const email = (location.state as PhoneLoginRouteProps)?.email;

  /**
   * Carrier prefill (Verified) needs only name + verified phone, so run it
   * right after registration. On a hit, review everything on one screen;
   * on a miss, the onboarding machine collects DOB and the rest manually.
   */
  const tryVerifiedPrefill = async () => {
    if (!email) return false;

    try {
      const { data } = await fetchVerifiedPrefill();
      const prefillData = data?.userPrefillData;

      // The summary flow only makes sense when Verified returned the core
      // identity: name, DOB and a full address. Anything less skips the
      // summary and the machine collects fields manually. SSN is optional –
      // when absent, the machine asks for just the SSN afterwards.
      const hasPrefillData = Boolean(
        prefillData?.firstName &&
        prefillData.lastName &&
        prefillData.dateOfBirth &&
        prefillData.address?.street &&
        prefillData.address.city &&
        prefillData.address.state &&
        prefillData.address.postalCode,
      );

      if (!hasPrefillData) return false;

      setPrefillProps({
        firstName: prefillData?.firstName,
        lastName: prefillData?.lastName,
        dateOfBirth: prefillData?.dateOfBirth,
        email: prefillData?.email,
        phoneNumber: prefillData?.phoneNumber,
        address: prefillData?.address?.street,
        address2: prefillData?.address?.unitNo,
        city: prefillData?.address?.city,
        state: prefillData?.address?.state,
        zipCode: prefillData?.address?.postalCode,
        ssn: formatMaskedSsnNumber(prefillData?.ssn ?? ""),
      });

      const { data: user } = await fetchUser();
      localStorage.setItem(StorageKey.User, JSON.stringify(user));

      navigate(Routes.Prefill, { replace: true });
      return true;
    } catch {
      return false;
    }
  };

  const handleVerification = async (code: string) => {
    if (code.length !== CODE_LENGTH) return showErrorBanner();

    try {
      setLoading(true);

      const { data } = await loginWithPhone({
        variables: {
          phoneNumber,
          code,
          email,
        },
      });

      const token = data?.loginWithPhone.sessionJwt;

      if (token) {
        localStorage.setItem(StorageKey.Token, token);

        if (email) {
          // New registration: record terms consent (mobile parity).
          createTermsConsent({
            variables: { viewedTimestamp: new Date() },
          }).catch(() => undefined);

          // Submit the name collected during the entry flow. Verified
          // prefill keys on name + phone, and setting it here also lets
          // the onboarding machine skip the redundant name screen.
          const { firstName, lastName, preferredName } = useEntryIntakeStore.getState().intake;

          if (firstName && lastName) {
            try {
              await setUserIdentity({
                variables: {
                  firstName,
                  lastName,
                  preferredName: preferredName || undefined,
                },
              });
            } catch {
              // Non-fatal: the machine will collect the name later.
            }
          }
        }

        const wentToReview = await tryVerifiedPrefill();

        if (!wentToReview) {
          await navigateToNextRoute();
        }
      } else {
        showErrorBanner();
      }
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.PhoneLoginRouteTitle),
    subtitle: i18n.t(Translations.PhoneLoginRouteSubtitle, {
      number: formatPhoneNumber(phoneNumber),
    }),
    cta: {
      primary: {
        title: i18n.t(Translations.PhoneLoginRoutePrimaryCTA),
        onClick: () => handleVerification(code),
        disabled: code.length !== CODE_LENGTH,
        loading,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <CodeInput
        length={CODE_LENGTH}
        value={code}
        onChangeText={setCode}
        onComplete={(code: string) => handleVerification(code)}
      />
    </OnboardingLayout>
  );
};
